import { ReactNode, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Pagination, RegionState } from '@freelancelabs/components';

import ListCard from '../../components/ListCard';
import { SortDirection } from '../../graphql/__generated__/schema';
import { AccountsDocument } from '../../graphql/__generated__/types';
import AccountsTable from './AccountsTable';
import { accountsRefusalOf } from './accountsRefusal';

const PAGE_SIZE = 25;

interface Props {
    /** Recherche déjà débouncée, câblée sur `accounts(search:)`. */
    debouncedSearch: string;
    /** Filtre portail, câblé sur `accounts(portalEnabled:)`. */
    portalEnabled: boolean | null;
    moduleTotal: number | null;
    /** La vue à plat montre la société mère et rend le nom triable. */
    showCompany?: boolean;
    /** Rend la liste des seuls comptes indépendants (racine = soi seul). */
    independentOnly?: boolean;
    /** Le nom de la scroll region de la carte. */
    label: string;
    /** Titre et phrase de l'état vide non filtré : chaque liste nomme son cas. */
    emptyTitle: string;
    emptyDescription: ReactNode;
}

/**
 * Une liste plate de comptes, paginée et comptée (PCG-333) : la vue à plat, et la
 * section « Comptes indépendants » de la vue groupée. Les deux interrogent `accounts`
 * avec des variables différentes et rendent le même tableau ; les écrire deux fois
 * donnerait deux idées du compteur et de l'état vide.
 *
 * Reprend la logique de PCG-141 : recherche déjà débouncée en amont, `cache-and-network`
 * parce que les faits d'une ligne se changent depuis la fiche, et squelette sur
 * l'ABSENCE de données et non sur `loading`, sinon chaque retour vide un tableau qu'on
 * lit.
 */
const AccountsList = ({
    debouncedSearch,
    portalEnabled,
    moduleTotal,
    showCompany = false,
    independentOnly = false,
    label,
    emptyTitle,
    emptyDescription,
}: Props) => {
    const [direction, setDirection] = useState<SortDirection>(SortDirection.Asc);
    const [page, setPage] = useState(1);

    // Tout changement de critère renvoie en première page : rester en page 3 d'un
    // résultat qui n'en a plus qu'une montre un tableau vide et se lit comme un bug.
    useEffect(() => setPage(1), [debouncedSearch, portalEnabled, direction]);

    const { data, loading, error } = useQuery(AccountsDocument, {
        variables: {
            search: debouncedSearch || null,
            portalEnabled,
            independentOnly: independentOnly || null,
            direction,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        fetchPolicy: 'cache-and-network',
    });

    const result = data?.accounts;
    const items = result?.items ?? [];
    // Jamais `items.length` : ça compterait une page. Le total de l'API suit la
    // recherche et le filtre, donc il compte ce que le lecteur pagine.
    const total = result?.total ?? 0;

    const filtered = Boolean(debouncedSearch) || portalEnabled !== null;
    const showSkeleton = loading && !data;
    const refusal = error ? accountsRefusalOf(error) : null;

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + items.length;
    const summary = c('Accounts list').ngettext(
        msgid`${rangeStart}–${rangeEnd} of ${total} account`,
        `${rangeStart}–${rangeEnd} of ${total} accounts`,
        total
    );

    const state = refusal ? (
        // Trois états et non deux : une erreur rendue en absence renverrait l'opérateur
        // convaincu que son périmètre est vide, alors qu'un périmètre vide est justement
        // une page vide et non un refus.
        <RegionState
            tone="error"
            title={refusal.title}
            description={
                <>
                    {refusal.guidance}
                    {refusal.detail && (
                        <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                            {refusal.detail}
                        </span>
                    )}
                </>
            }
        />
    ) : !showSkeleton && items.length === 0 && filtered ? (
        <RegionState
            title={c('Accounts').t`No account matches these criteria.`}
            description={c('Accounts')
                .t`The search reads the account name and its SIREN, which is missing on about a fifth of the parc, so an account can be there without matching. It does not read the name of the company: search for the subsidiary rather than for the group.`}
        />
    ) : !showSkeleton && items.length === 0 && !filtered ? (
        <RegionState title={emptyTitle} description={emptyDescription} />
    ) : null;

    return (
        <ListCard
            label={label}
            footer={
                !showSkeleton &&
                !error &&
                items.length > 0 && (
                    <div className="px-4">
                        <Pagination
                            page={page}
                            pageSize={PAGE_SIZE}
                            totalCount={total}
                            summary={summary}
                            onPageChange={setPage}
                        />
                    </div>
                )
            }
        >
            <AccountsTable
                rows={items}
                moduleTotal={moduleTotal}
                showCompany={showCompany}
                sortDirection={showCompany ? direction : undefined}
                onSortDirection={showCompany ? setDirection : undefined}
                loading={showSkeleton}
                hasError={Boolean(error)}
                state={state}
            />
        </ListCard>
    );
};

export default AccountsList;
